import { Package } from '../types/Package';
import { API_BASE_URL } from './apiBase';

const SERVICES_KEY = 'admin_services';

const allowLocalFallback = process.env.REACT_APP_ALLOW_LOCAL_FALLBACK === 'true';

export async function loadServicesFromServer(): Promise<Package[]> {
  try {
    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    // Add cache-busting query parameter and no-cache option to prevent stale data on Render
    const cacheBuster = Date.now();
    const url = `${API_BASE_URL}/services?v=${cacheBuster}`;
    console.log('📦 [CLIENT] Fetching services from:', url);
    const res = await fetch(url, {
      signal: controller.signal,
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    clearTimeout(timeoutId);
    
    console.log('📦 [CLIENT] Response status:', res.status, res.statusText);
    if (!res.ok) throw new Error(`Failed to load services: ${res.status} ${res.statusText}`);
    const data = await res.json();
    console.log('📦 [CLIENT] Raw services response:', JSON.stringify(data, null, 2));
    const serverPackages = Array.isArray(data) ? data as Package[] : [];
    console.log('📦 [CLIENT] Parsed packages:', serverPackages.length);
    serverPackages.forEach((pkg, idx) => {
      console.log(`📦 [CLIENT] Package ${idx + 1}: "${pkg.name}" - addons: ${pkg.addons?.length || 0}`, 
        pkg.addons?.map(a => ({ id: a.id, name: a.name, price: a.price })));
    });
    
    return serverPackages;
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      console.warn('Service API request timed out');
    } else {
      console.warn('Failed to load services from API:', e);
    }

    if (allowLocalFallback) {
      const localServices = loadServicesFromLocal();
      if (localServices.length > 0) {
        console.warn('⚠️ Local fallback enabled: loading services from localStorage');
        return localServices;
      }
    }

    return [];
  }
}

export function loadServicesFromLocal(): Package[] {
  try {
    const raw = localStorage.getItem(SERVICES_KEY);
    console.log('📱 Local storage data for', SERVICES_KEY, ':', raw ? 'exists' : 'not found');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    console.log('📱 Parsed local data:', parsed.length, 'packages');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('📱 Error parsing local data:', e);
    return [];
  }
}

export async function saveServicesToServer(services: Package[]): Promise<boolean> {
  // Always save to localStorage as backup, regardless of server save result
  saveServicesToLocal(services);
  
  try {
    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const res = await fetch(`${API_BASE_URL}/services`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(services),
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    
    if (res.ok) {
      console.log('✅ Packages saved to server and localStorage');
    } else {
      console.warn('⚠️ Server save failed, but data saved to localStorage');
    }
    
    return res.ok;
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      console.warn('Service API save request timed out, persisted locally');
    } else {
      console.warn('Saving services to server failed, persisted locally:', e);
    }
    // Already saved to localStorage above, so we're good
    return false;
  }
}

export function saveServicesToLocal(services: Package[]): void {
  localStorage.setItem(SERVICES_KEY, JSON.stringify(services));
}

export function findServiceBySlug(services: Package[], slug: string): Package | undefined {
  if (!slug) return undefined;
  
  // First try exact slug match
  let found = services.find(s => (s.slug || '').toLowerCase() === slug.toLowerCase());
  
  // If not found, try generating slug from name
  if (!found) {
    found = services.find(s => {
      const generatedSlug = s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      return generatedSlug === slug.toLowerCase();
    });
  }
  
  return found;
}


